import { apiClient } from "../../../services/apiClient";
import { logger } from "../../../services/logger";

const BASE_PATH = "/admin/auto-tune";

export interface AutoTuneStatus {
  enabled: boolean;
  mode: string;
  lastAnalysis: string | null;
  totalRecommendationsPending: number;
  totalRecommendationsApplied: number;
  changesToday: number;
  maxDailyChanges: number;
}

export interface Recommendation {
  id: string;
  settingKey: string;
  settingLabel: string;
  filterCategory: string;
  filterName: string | null;
  currentValue: string;
  suggestedValue: string;
  valueType: string;
  confidence: number;
  sampleSize: number;
  reason: string;
  evidence: Record<string, unknown>;
  currentWinRate: number | null;
  currentFalsePositiveRate: number | null;
  expectedWinRateAfter: number | null;
  expectedImpact: string | null;
  status: string;
  appliedAt: string | null;
  appliedBy: string | null;
  rolledBackAt: string | null;
  rollbackReason: string | null;
  createdAt: string | null;
  expiresAt: string | null;
}

export interface FilterStats {
  filterCategory: string;
  filterName: string;
  total: number;
  passed: number;
  failed: number;
  passRate: number;
  failRate: number;
}

export interface RejectedAnalysis {
  total: number;
  falsePositive: number;
  correctReject: number;
  partialMiss: number;
  inconclusive: number;
  falsePositiveRate: number;
  totalMissedProfitUsdt: number;
}

export interface HistoryItem {
  id: string;
  settingKey: string;
  settingLabel: string;
  filterCategory: string;
  filterName: string | null;
  currentValue: string;
  suggestedValue: string;
  status: string;
  confidence: number | null;
  appliedAt: string | null;
  appliedBy: string | null;
  rolledBackAt: string | null;
  rollbackReason: string | null;
  createdAt: string;
}

interface RecommendationListWrapper {
  recommendations: Recommendation[];
  count: number;
  refreshedAt: string;
}

interface FilterStatsListWrapper {
  filters: FilterStats[];
  count: number;
  refreshedAt: string;
}

interface HistoryListWrapper {
  items: HistoryItem[];
  count: number;
  refreshedAt: string;
}

export interface AnalysisResult {
  status: string;
  recommendationsGenerated: number;
  rejectedAnalyzed: number;
}

export interface RejectedAnalysisResult {
  analyzed: number;
}

export interface ModeChangeResult {
  mode: string;
}

async function handleError(error: unknown, context: string): Promise<never> {
  const errorMessage =
    (error as any)?.response?.data?.detail ||
    (error as Error)?.message ||
    `${context}: Unknown error`;

  const enriched = new Error(errorMessage) as Error & { statusCode?: number };
  enriched.statusCode = (error as any)?.response?.status;
  logger.error(`${context}:`, error instanceof Error ? error : new Error(String(error)));
  throw enriched;
}

export async function getStatus(): Promise<AutoTuneStatus> {
  try {
    const response = await apiClient.get<AutoTuneStatus>(`${BASE_PATH}/status`);
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to fetch auto-tune status");
  }
}

export async function getRecommendations(
  category?: string,
  statusFilter?: string
): Promise<Recommendation[]> {
  try {
    const params: Record<string, string> = {};
    if (category) params.category = category;
    if (statusFilter) params.status = statusFilter;
    const response = await apiClient.get<RecommendationListWrapper>(
      `${BASE_PATH}/recommendations`,
      { params }
    );
    return response.data.recommendations;
  } catch (error) {
    return handleError(error, "Failed to fetch recommendations");
  }
}

export async function applyRecommendation(id: string): Promise<Record<string, unknown>> {
  try {
    const response = await apiClient.post<Record<string, unknown>>(
      `${BASE_PATH}/recommendations/${id}/apply`
    );
    return response.data;
  } catch (error) {
    return handleError(error, `Failed to apply recommendation ${id}`);
  }
}

export async function rejectRecommendation(id: string): Promise<Record<string, unknown>> {
  try {
    const response = await apiClient.post<Record<string, unknown>>(
      `${BASE_PATH}/recommendations/${id}/reject`
    );
    return response.data;
  } catch (error) {
    return handleError(error, `Failed to reject recommendation ${id}`);
  }
}

export async function rollbackRecommendation(
  id: string,
  reason: string
): Promise<Record<string, unknown>> {
  try {
    const response = await apiClient.post<Record<string, unknown>>(
      `${BASE_PATH}/recommendations/${id}/rollback`,
      { reason }
    );
    return response.data;
  } catch (error) {
    return handleError(error, `Failed to rollback recommendation ${id}`);
  }
}

export async function getFilterStats(
  category?: string,
  filterName?: string
): Promise<FilterStats[]> {
  try {
    let url = `${BASE_PATH}/filter-stats`;
    if (category && filterName) {
      url = `${BASE_PATH}/filter-stats/${category}/${filterName}`;
    } else if (category) {
      url = `${BASE_PATH}/filter-stats/${category}`;
    }
    const response = await apiClient.get<FilterStatsListWrapper>(url);
    return response.data.filters;
  } catch (error) {
    return handleError(error, "Failed to fetch filter stats");
  }
}

export async function getRejectedAnalysis(): Promise<RejectedAnalysis> {
  try {
    const response = await apiClient.get<RejectedAnalysis>(
      `${BASE_PATH}/rejected-analysis`
    );
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to fetch rejected analysis");
  }
}

export async function runAnalysis(): Promise<AnalysisResult> {
  try {
    const response = await apiClient.post<AnalysisResult>(
      `${BASE_PATH}/analyze`,
      {},
      { timeout: 300000 }
    );
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to run analysis");
  }
}

export async function runRejectedAnalysis(): Promise<RejectedAnalysisResult> {
  try {
    const response = await apiClient.post<RejectedAnalysisResult>(
      `${BASE_PATH}/rejected-analysis/run`,
      {},
      { timeout: 300000 }
    );
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to run rejected analysis");
  }
}

export async function getHistory(): Promise<HistoryItem[]> {
  try {
    const response = await apiClient.get<HistoryListWrapper>(
      `${BASE_PATH}/history`
    );
    return response.data.items;
  } catch (error) {
    return handleError(error, "Failed to fetch history");
  }
}

export async function getSettings(): Promise<Record<string, unknown>> {
  try {
    const response = await apiClient.get<Record<string, unknown>>(
      `${BASE_PATH}/settings`
    );
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to fetch settings");
  }
}

export async function updateSettings(
  settings: Record<string, unknown>
): Promise<Record<string, unknown>> {
  try {
    const response = await apiClient.put<Record<string, unknown>>(
      `${BASE_PATH}/settings`,
      settings
    );
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to update settings");
  }
}

export async function changeMode(mode: string): Promise<ModeChangeResult> {
  try {
    const response = await apiClient.post<ModeChangeResult>(
      `${BASE_PATH}/mode`,
      { mode }
    );
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to change mode");
  }
}

export interface LearningCycleResult {
  status: string;
  processedTrades: number;
  durationSeconds: number;
  startedAt: string;
  completedAt?: string;
  failedAt?: string;
  error?: string;
  autoTune?: Record<string, unknown>;
  learningBridge?: Record<string, unknown>;
  bridgeOptimizationSuggestions: number;
}

export async function runLearningCycle(): Promise<LearningCycleResult> {
  try {
    const response = await apiClient.post<LearningCycleResult>(
      `${BASE_PATH}/learning-cycle`,
      {},
      { timeout: 600000 }
    );
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to run learning cycle");
  }
}
